import { useEffect, useRef, useState } from "react";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnMenuFilter,
  GridColumnMenuProps,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridNoRecords,
} from "@progress/kendo-react-grid";
import {
  setExpandedState,
  setGroupIds,
} from "@progress/kendo-react-data-tools";
import { DataResult, process, State } from "@progress/kendo-data-query";
import { SubmitStatus } from "../../core/formik-model-dialog.component";
import { API_END_POINT } from "../../constants";
import { GridCommandCell } from "../../core/grid-command-cell.component";
import { LoadingIndicator } from "../../core/loading-indicator.component";
import { DeleteModelDialog, DeletePostDataInfo, DeleteStatus } from "../../core/delete-model-dialog.component";
import UserTypeModel from "../../models/masters/userdetails-model";
import { useGetUserTypes } from "../../apiclient/master/useGetUserTypeApi";
import UserDetailsEntry from "./userdetails-entry";
import UserDetailsModel from "../../models/user-security/userdetails-model";
import { useGetUserDetails } from "../../apiclient/admin/useGetUserDetailsApi";
import UserDetailsWebEntry from "./userdetailsweb-entry";

export const addOrUpdateArrayItem = (data: any[], item: any, key: string) => {
  const newData = data ? [...data] : [];
  const index = data?.findIndex((d) => d[key] === item[key]);
  if (index > -1) {
    newData.splice(index, 1, item);
  } else {
    newData.push(item);
  }
  return newData;
};

const UserDetailsPage: React.FC = () => {
  const initialDataState: State = {
    take: 100,
    skip: 0,
    group: [],
  };

  const [checkbox1, setCheckbox1] = useState<boolean>(false); // Web checkbox
  const [checkbox2, setCheckbox2] = useState<boolean>(false); // Agent checkbox

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [collapsedState, setCollapsedState] = useState<string[]>([]);
  const [resultData, setResultData] = useState<DataResult>();
  const [editModal, setEditModal] = useState<boolean>(false);
  const [editData, setEditData] = useState<UserDetailsModel | null>(null); // Web entry data
  const [editAgentData, setEditAgentData] = useState<UserDetailsModel | null>(null); // Agent entry data

  const exportExcel = useRef<ExcelExport | null>(null);

  const { data: result, isLoading, isError, refetch: reloadUserType } = useGetUserDetails();

  useEffect(() => {
    if (result) {
      const processedData = processWithGroups(result, initialDataState);
      setResultData(processedData);
    }
  }, [result]);

  const processWithGroups = (data: UserDetailsModel[], dataState: State) => {
    const groups = dataState.group;
    dataState.group = groups;
    const newDataState = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const onSaveComplete = (status: SubmitStatus, data: UserDetailsModel) => {
    setEditModal(false); // Close the modal after saving the data
    setEditData(null); // Reset data for future entries
    setEditAgentData(null); // Reset agent data if needed
    if (status === SubmitStatus.Completed) {
      reloadUserType();
      const newDataState = processWithGroups(result, dataState); // Re-fetch data after saving
      setResultData(newDataState);
    }
  };

  const excelExport = () => {
    if (exportExcel.current !== null) {
      exportExcel.current.save();
    }
  };

  const onAddNew = () => {
    setEditModal(true);
    setEditData(null); // Reset the entry data when adding a new user
    setEditAgentData(null); // Reset agent data if needed

    if (checkbox1) {
      setEditData(new UserDetailsModel()); // Ensure a fresh UserDetailsModel is set for Web
    } else if (checkbox2) {
      setEditAgentData(new UserDetailsModel()); // Ensure a fresh UserDetailsModel is set for Agent
    } else {
      alert("Please select either Web or Agent.");
    }
  };

  const onEdit = (data: UserDetailsModel) => {
    setEditModal(true);
    setEditData(data);
  };

  const onDeleteComplete = (status: DeleteStatus, data: UserDetailsModel) => {
    if (status === DeleteStatus.Completed) {
      reloadUserType();
      const newDataState = processWithGroups(result, dataState);
      setResultData(newDataState);
    }
  };

  const ColumnMenu = (props: GridColumnMenuProps) => {
    return (
      <div>
        <GridColumnMenuFilter {...props} expanded={true} />
      </div>
    );
  };

  const CommandCell = (props: GridCellProps) => (
    <GridCommandCell
      {...props}
      edit={onEdit}
      add={onAddNew}
      dataItem={props.dataItem}
      idPropertyName="userTypeId"
      editPermissionName={null}
      deletePermissionName={null}
    />
  );

  const expandChange = (event: GridExpandChangeEvent) => {
    const item = event.dataItem;

    if (item.groupId) {
      const newCollapsedIds = !event.value
        ? [...collapsedState, item.groupId]
        : collapsedState.filter((groupId) => groupId !== item.groupId);
      setCollapsedState(newCollapsedIds);
    }
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(result, event.dataState);
    setResultData(newDataState);
    setDataState(event.dataState);
  };

  const ColumnFormatBoolean = (props: any) => {
    return (
      <td
        {...props.tdProps}
        colSpan={1}
        style={{
          color: "black",
        }}
      >
        {props.children === "true" ? (
          <span style={{ color: "green", fontWeight: "bold" }}>Yes</span>
        ) : (
          <span style={{ color: "red", fontWeight: "bold" }}>No</span>
        )}
      </td>
    );
  };

  let newData: any = [];
  if (resultData) {
    newData = setExpandedState({
      data: resultData.data,
      collapsedIds: collapsedState,
    });
  }

  if (isError) {
    return (
      <div>
        <h4>Error Loading Page..</h4>
      </div>
    );
  }

  return (
    <>
      <div
        style={{ minHeight: "600px" }}
        className="card p-3 border-0 m-3 sw-shadow radius-15"
      >
        <div className="top-sec-align row">
          <h5 className="text-normal title-web mb-0">User Details</h5>
        </div>
        <div className="top-sec-align row">
          <div className="d-flex" style={{ justifyContent: "space-between" }}>
            {/* Add Checkboxes Here */}
            <div>
              <label style={{ fontSize: "18px" }}>
                <input
                  type="checkbox"
                  checked={checkbox1}
                  style={{ marginRight: "10px" }}
                  onChange={() => {
                    setCheckbox1(true);
                    setCheckbox2(false);
                  }}
                />
                Web
              </label>
            </div>

            <div>
              <label style={{ fontSize: "18px" }}>
                <input
                  type="checkbox"
                  checked={checkbox2}
                  style={{ marginRight: "10px" }}
                  onChange={() => {
                    setCheckbox2(true);
                    setCheckbox1(false);
                  }}
                />
                Agent
              </label>
            </div>

            <button
              title="Add new"
              className="btn btn-primary btn-sm ms-2"
              onClick={onAddNew}
            >
              Add User Details
            </button>
          </div>
        </div>

        <LoadingIndicator isLoading={isLoading} />

        {/* Conditionally Render UserDetailsEntry or AgentDetailsEntry */}
        {editModal && checkbox1 && (
          <UserDetailsWebEntry data={editData} onClose={onSaveComplete} />
        )}
        {editModal && checkbox2 && (
          <UserDetailsEntry data={editAgentData} onClose={onSaveComplete} />
        )}

        <div>
          <ExcelExport
            fileName={"UserType_List"}
            data={newData}
            ref={exportExcel}
          >
            <ExcelExportColumn width={100} field="name" title=" Name" />
            <ExcelExportColumn width={150} field="union" title="Union" />
          </ExcelExport>
        </div>

        <Grid
          data={newData}
          total={resultData?.total}
          sortable={true}
          onDataStateChange={dataStateChange}
          {...dataState}
          onExpandChange={expandChange}
          expandField="expanded"
          resizable={true}
          columnVirtualization={false}
        >
          <GridColumn width={"250px"} field="userName" title="User Name" columnMenu={ColumnMenu} />
          <GridColumn width={"250px"} field="mobile" title="Mobile" columnMenu={ColumnMenu} />
          <GridColumn width={"250px"} field="email" title="E-mail" />
          <GridColumn width={"100px"} field="isActive" title="Active ?" cells={{ data: ColumnFormatBoolean }} />
          <GridNoRecords>
            <div className="mt-2" style={{ width: "100%", height: "500px" }}>
              <center>
                <h5>No data found</h5>
              </center>
            </div>
          </GridNoRecords>
        </Grid>
      </div>
    </>
  );
};

export default UserDetailsPage;
